import React, { useState, useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { supabase } from '../../supabaseClient';
import ExpenseModal from './ExpenseModal';
import { buildApiUrl } from '../../utils/apiBase';

const callExpensesApi = async (body) => {
  const accessToken = liff?.getAccessToken?.();
  if (!accessToken) throw new Error('No LINE access token');
  const res = await fetch(buildApiUrl('/api/expenses'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken, ...body }),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 409) {
    const err = new Error('duplicate');
    err.code = json.error;
    throw err;
  }
  if (!res.ok) throw new Error(json.error || `API error ${res.status}`);
  return json;
};

const formatMoney = (amount) =>
  Number(amount).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const formatDateTimeTH = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BRAND_ORANGE = '#FF6B35';

const ExpenseDetail = ({ userProfile, appUser }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [friends, setFriends] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const expenseMode = urlParams.get('expenseMode') || '';
  const prefillNote = urlParams.get('prefillNote') || '';
  const prefillAmount = urlParams.get('prefillAmount') || '';

  const slipToken = urlParams.get('slipToken') || '';

  const expenseIds = (() => {
    const raw = urlParams.get('expenseIds') || '';
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  })();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchExpenses = useCallback(async () => {
    if (!expenseIds.length) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .in('id', expenseIds)
        .order('occurred_at', { ascending: true });
      if (error) throw error;
      setExpenses(data || []);
    } catch (e) {
      console.error('ExpenseDetail fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [expenseIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentMethods = useCallback(async () => {
    if (!appUser?.id) return;
    const { data } = await supabase
      .from('payment_methods')
      .select('*')
      .or(`owner_id.eq.${appUser.id},user_id.eq.${userProfile?.userId || ''}`);
    setPaymentMethods(data || []);
  }, [appUser?.id, userProfile?.userId]);

  const fetchFriends = useCallback(async () => {
    if (!appUser?.id) return;
    const { data, error } = await supabase
      .from('friendships')
      .select(`
        id,
        user_id_1, user_id_2,
        friend1:users!user_id_1(id, display_name, picture_url, username, line_user_id),
        friend2:users!user_id_2(id, display_name, picture_url, username, line_user_id)
      `)
      .or(`user_id_1.eq.${appUser.id},user_id_2.eq.${appUser.id}`)
      .eq('status', 'accepted');
    if (error) { console.error('fetchFriends error:', error); return; }
    const list = (data || []).map((f) => {
      const friend = f.user_id_1 === appUser.id ? f.friend2 : f.friend1;
      return friend;
    }).filter(Boolean);
    setFriends(list);
  }, [appUser?.id]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchPaymentMethods(); }, [fetchPaymentMethods]);
  useEffect(() => { fetchFriends(); }, [fetchFriends]);

  useEffect(() => {
    if (expenseMode === 'add') setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSlipExpense = () => {
    setIsAddMode(true);
    setEditingExpense({
      amount: prefillAmount,
      note: prefillNote,
      currency: 'THB',
      occurred_at: new Date().toISOString(),
      payment_method: 'เงินสด',
      payment_method_id: null,
      _isNew: true,
    });
    setIsModalOpen(true);
  };

  const isOwner = (expense) => {
    if (!appUser) return false;
    if (expense.payer_user_id && expense.payer_user_id === appUser.id) return true;
    const lineId = appUser.line_user_id || userProfile?.userId;
    if (lineId && expense.user_id === lineId) return true;
    return false;
  };

  const handleEdit = (expense) => {
    if (!isOwner(expense)) {
      showToast('แก้ไขได้เฉพาะรายการของตัวเองเท่านั้น', 'error');
      return;
    }
    setIsAddMode(false);
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    if (!editingExpense) return;
    if (!isAddMode && !isOwner(editingExpense)) {
      showToast('แก้ไขได้เฉพาะรายการของตัวเองเท่านั้น', 'error');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        note: formData.note,
        currency: formData.currency,
        occurred_at: new Date(formData.occurredAt).toISOString(),
        payment_method_id: formData.paymentMethodId || null,
        payment_method: formData.paymentMethod,
      };

      if (isAddMode) {
        await callExpensesApi({
          action: 'create',
          payload: {
            ...payload,
            payer_user_id: appUser?.id || null,
            trip_id: null,
            slip_token: slipToken || null,
          },
          slipToken: slipToken || null,
        });
        showToast('บันทึกรายจ่ายแล้ว');
      } else {
        await callExpensesApi({ action: 'update', expenseId: editingExpense.id, payload });
        showToast('แก้ไขเรียบร้อยแล้ว');
      }

      setIsModalOpen(false);
      setEditingExpense(null);
      setIsAddMode(false);
      if (!isAddMode) await fetchExpenses();
    } catch (e) {
      console.error('save error:', e);
      if (e.code === 'duplicate_slip' || e.code === 'duplicate_entry') {
        showToast('ถึงเธอจะความจำสั้น แต่ไม่ต้องบันทึกซ้ำ ปลาทองจำได้จ้ะ 🐟', 'error');
        setIsModalOpen(false);
        setEditingExpense(null);
        setIsAddMode(false);
      } else {
        showToast('บันทึกไม่สำเร็จ ลองอีกที', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    const expense = expenses.find((e) => e.id === id);
    if (expense && !isOwner(expense)) {
      showToast('ลบได้เฉพาะรายการของตัวเองเท่านั้น', 'error');
      return;
    }
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await callExpensesApi({ action: 'delete', expenseId: deleteId });
      showToast('ลบรายการเรียบร้อยแล้ว');
      setIsDeleteModalOpen(false);
      setDeleteId(null);
      await fetchExpenses();
    } catch (e) {
      console.error('delete error:', e);
      showToast('ลบไม่สำเร็จ ลองอีกที', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    try { liff.closeWindow(); } catch { window.history.back(); }
  };

  const total = expenses.reduce((sum, e) => sum + Number(e.amount ?? 0), 0);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fa', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{ backgroundColor: BRAND_ORANGE, padding: '20px 16px 16px', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: '0.5px' }}>
              {expenseMode === 'add' ? 'แก้ไขรายจ่าย' : 'รายจ่ายที่เพิ่งบันทึก'}
            </p>
            <h1 style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>
              {expenseMode === 'add' && prefillNote ? prefillNote : loading ? '...' : `${expenses.length} รายการ`}
            </h1>
          </div>
          <button
            onClick={handleClose}
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20, padding: '8px 16px', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >
            เสร็จแล้ว
          </button>
        </div>
      </div>

      {/* Slip-add prompt */}
      {expenseMode === 'add' && !loading && (
        <div style={{ padding: '16px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '20px 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', textAlign: 'center' }}>
            <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: '#111' }}>{prefillNote || 'รายจ่ายจากสลิป'}</p>
            <p style={{ margin: '0 0 18px', fontSize: 22, fontWeight: 700, color: BRAND_ORANGE }}>
              {Number(prefillAmount || 0).toLocaleString('th-TH')} บาท
            </p>
            <button
              onClick={handleAddSlipExpense}
              disabled={isModalOpen}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', backgroundColor: isModalOpen ? '#ccc' : BRAND_ORANGE, color: '#fff', fontWeight: 700, fontSize: 15, cursor: isModalOpen ? 'default' : 'pointer' }}
            >
              บันทึกสำหรับฉัน
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div style={{ padding: expenseMode === 'add' ? '0 16px 16px' : '16px' }}>
        {expenseMode === 'add' ? null : loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>กำลังโหลด...</div>
        ) : expenses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>ไม่พบรายการ</div>
        ) : (
          expenses.map((expense) => (
            <div key={expense.id} style={{ backgroundColor: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {expense.note || 'ไม่ระบุ'}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888' }}>
                  {formatDateTimeTH(expense.occurred_at)}
                </p>
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: BRAND_ORANGE }}>
                  {formatMoney(expense.amount)} ฿
                </p>
              </div>
              {isOwner(expense) ? (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => handleEdit(expense)}
                    style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${BRAND_ORANGE}`, backgroundColor: 'transparent', color: BRAND_ORANGE, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                  >
                    แก้ไข
                  </button>
                  <button
                    onClick={() => handleDeleteClick(expense.id)}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e53935', backgroundColor: 'transparent', color: '#e53935', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                  >
                    ลบ
                  </button>
                </div>
              ) : (
                <span style={{ fontSize: 11, color: '#bbb', flexShrink: 0 }}>ของคนอื่น</span>
              )}
            </div>
          ))
        )}

        {/* Total */}
        {!loading && expenses.length > 0 && (
          <div style={{ backgroundColor: '#fff8f5', borderRadius: 14, padding: '14px 16px', marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#888' }}>รวมทั้งหมด</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: BRAND_ORANGE }}>{formatMoney(total)} บาท</span>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <ExpenseModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingExpense(null); }}
        onSave={handleSave}
        editingExpense={editingExpense}
        userProfile={userProfile}
        appUser={appUser}
        savedPayers={[]}
        paymentMethods={paymentMethods}
        onPaymentMethodsChange={fetchPaymentMethods}
        loading={loading}
        onShowToast={showToast}
        friends={friends}
        shareCandidates={friends}
        onDeleteFriend={() => {}}
        onDelete={handleDeleteClick}
      />

      {/* Delete Confirm Modal */}
      {isDeleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'var(--color-glass-strong)', border: '1px solid rgba(255,255,255,0.8)', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(30,70,90,0.16)', textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบ</h3>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '25px' }}>คุณต้องการลบรายการนี้ใช่หรือไม่?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button onClick={() => setIsDeleteModalOpen(false)} style={{ padding: '10px 20px', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>
                ยกเลิก
              </button>
              <button onClick={confirmDelete} style={{ padding: '10px 20px', backgroundColor: 'var(--color-danger)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 14px rgba(224,86,75,0.35)' }}>
                {loading ? 'กำลังลบ...' : 'ลบรายการ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)', backgroundColor: toast.type === 'error' ? '#e53935' : '#333', color: '#fff', padding: '12px 24px', borderRadius: 8, fontSize: 14, zIndex: 3000, whiteSpace: 'nowrap' }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ExpenseDetail;
